import _defineProperty from "/builds/speachme/speachmeDesktop/speach-front/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import { __decorate } from "tslib";
import { ElMessage } from "element-plus";
import Editor from "@tinymce/tinymce-vue";
import StandardButton from "@/components/buttons/StandardButton.vue";
import { confirmation } from "@/util/dialog";
import { Component, Prop, Vue } from "vue-facing-decorator";
import { useCustomCheckbox } from "@/components/tinyMce/plugins/customCheckbox/customCheckbox";
import { useCustomButton } from "@/components/tinyMce/plugins/customButton/customButton";
import { LangEnum } from "@/models/LangEnum";
import FlipCardImageDialog from "@/components/pageEditor/FlipCardImageDialog.vue";
import WElmessage from "@/components/wrappers/w-elmessage.vue";
import { useCustomImageUploader } from "@/components/tinyMce/plugins/customImageUploader/customImageUploader";
import FlipCardPlugin from "@/components/tinyMce/plugins/flipcard/plugin";
import ConfirmDialog from "@/components/dialogs/ConfirmDialog.vue";
let WTinyMce = class WTinyMce extends Vue {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "editorId", void 0);
    _defineProperty(this, "customConfig", void 0);
    _defineProperty(this, "text", void 0);
    _defineProperty(this, "displayEditorButtons", void 0);
    _defineProperty(this, "saveLabel", '');
    _defineProperty(this, "cancelLabel", '');
    _defineProperty(this, "initConfig", null);
    _defineProperty(this, "initialText", '');
    _defineProperty(this, "liveText", '');
    _defineProperty(this, "currentLang", '');
    _defineProperty(this, "editorInstance", null);
    _defineProperty(this, "flipCardEventContext", null);
    _defineProperty(this, "flipCardAddImgEventTriggered", false);
    _defineProperty(this, "flipCardDeleteEventTriggered", false);
    _defineProperty(this, "formatUiLangToTinyMceFormat", lang => {
      switch (lang?.toLowerCase()) {
        case LangEnum.FR:
          return 'fr_FR';
        case LangEnum.EN:
          return 'en';
        case LangEnum.ES:
          return 'es';
        case LangEnum.DE:
          return 'de';
        default:
          return 'en';
      }
    });
  }
  mounted() {
    this.currentLang = this.$i18n.locale.toLowerCase();
  }
  updated() {
    if (this.text && !this.initialText) {
      this.initialText = this.text;
      this.liveText = this.text;
    }
    if (this.customConfig && !this.initConfig) {
      this.initConfig = {
        branding: false,
        statusbar: false,
        language: this.formatUiLangToTinyMceFormat(this.currentLang),
        plugins: 'lists code preview link',
        menubar: false,
        relative_urls: false,
        convert_urls: false,
        // toolbar default options.
        toolbar: `undo redo | styles | bold italic underline strikethrough | alignleft aligncenter alignright alignjustify |
                            bullist numlist outdent indent | lgit ink | link`,
        toolbar_mode: 'wrap',
        placeholder: this.$t("documentEditor.placeholder"),
        content_style: `
          .mce-content-body {
            margin: 8px;
          }
          html, body {
            max-width: 100%;
            overflow-wrap: break-word;
            word-break: break-word;
          }
          * {
            box-sizing: border-box;
            max-width: 100%;
          }
          a {
            overflow-wrap: anywhere;
          }
        `,
        verify_html: false,
        // pour autoriser tous les elements html et attributs. On délègue la sanitization au backend
        ...this.customConfig
      };
      if (this.customConfig.allowFullScreenExpand) {
        if (!this.initConfig.plugins.includes('fullscreen')) {
          this.initConfig.plugins = `${this.initConfig.plugins} fullscreen`;
        }
        if (!this.initConfig.toolbar.includes('fullscreen')) {
          this.initConfig.toolbar = `${this.initConfig.toolbar} | fullscreen`;
        }
      }
      this.loadCustomPluginsConfigs();
    }
  }
  loadCustomPluginsConfigs() {
    const toolbar = this.initConfig.toolbar;
    const docComputedStyle = getComputedStyle(document.documentElement);
    const themeMainColor = docComputedStyle.getPropertyValue('--themeMainColor');
    const primaryColor = docComputedStyle.getPropertyValue('--primary');
    const $lightColor = docComputedStyle.getPropertyValue('--light');
    const $light1Color = docComputedStyle.getPropertyValue('--light1');
    const $light2Color = docComputedStyle.getPropertyValue('--light2');
    const $darkColor = docComputedStyle.getPropertyValue('--dark');
    const $black = docComputedStyle.getPropertyValue('--black');
    this.initConfig = {
      ...this.initConfig,
      setup: editor => {
        this.editorInstance = editor;
        // We load the custom requested plugins
        if (toolbar) {
          if (toolbar.includes('flipcard')) {
            FlipCardPlugin()(editor);
            this.handleFlipCardEvents(editor);
          }
          if (toolbar.includes('customchecklist')) {
            useCustomCheckbox(editor);
          }
          if (toolbar.includes('custombutton')) {
            useCustomButton(editor);
          }
          if (toolbar.includes('customimageupload')) {
            useCustomImageUploader(editor);
            this.handleCustomImageUploadEvents(editor);
          }
        }
        editor.on('ExecCommand', e => {
          if (e.command === 'InsertAccordion') {
            setTimeout(() => {
              const body = editor.getBody();
              const last = body.lastElementChild;
              const hasTrailingEmptyParagraph = last?.tagName === 'P' && last.textContent?.trim() === '';
              if (!hasTrailingEmptyParagraph) {
                const p = editor.dom.create('p', {}, '<br>');
                body.appendChild(p);
              }
            }, 0);
          }
        });
      }
    };
    if (toolbar) {
      // If flipcard plugin requested, then we set it parameters.
      if (toolbar.includes('flipcard')) {
        this.initConfig = {
          ...this.initConfig,
          flipcard_ui_mode: this.initConfig.flipcard_ui_mode || 'external',
          // 'native', 'embedded' ou Mode 'external' pour modale coté Vue.
          flipcard_i18n_locale: this.currentLang,
          flipcard_header_bg: this.initConfig.flipcard_header_bg || $lightColor,
          flipcard_footer_bg: this.initConfig.flipcard_footer_bg || $lightColor,
          flipcard_text_color: this.initConfig.flipcard_text_color || $black,
          flipcard_border_color: this.initConfig.flipcard_border_color || $light1Color,
          flipcard_drag_handle_color: this.initConfig.flipcard_drag_handle_color || $light2Color,
          flipcard_icon_color: this.initConfig.flipcard_icon_color || $black,
          flipcard_option_icon_color: this.initConfig.flipcard_option_icon_color || $light2Color,
          flipcard_separator_color: this.initConfig.flipcard_separator_color || $light2Color,
          flipcard_icon_hover_color: this.initConfig.flipcard_icon_hover_color || $light2Color,
          flipcard_option_hover_bg: this.initConfig.flipcard_option_hover_bg || $lightColor,
          flipcard_readmode_flipbtn_color: this.initConfig.flipcard_readmode_flipbtn_color || $darkColor,
          flipcard_readmode_flipbtn_hover_color: this.initConfig.flipcard_readmode_flipbtn_hover_color || $light2Color
        };
      }
      if (toolbar.includes('custombutton')) {
        this.initConfig = {
          ...this.initConfig,
          custombutton_i18n_locale: this.currentLang,
          custombutton_button_color: this.initConfig.button_color || themeMainColor
        };
      }
      if (toolbar.includes('customchecklist')) {
        this.initConfig = {
          ...this.initConfig,
          customchecklist_i18n_locale: this.currentLang,
          customchecklist_onCheckedColor: this.initConfig.customchecklist_onCheckedColor || primaryColor
        };
      }
      if (toolbar.includes('customimageupload')) {
        this.initConfig = {
          ...this.initConfig,
          customimageupload_i18n_locale: this.currentLang,
          customimageupload_uploadUrl: '/user/lib/photo'
        };
      }
    }
  }
  handleCustomImageUploadEvents(editor) {
    editor.on('customImageUploadNoFileSelected', () => {
      WElmessage.showErrorMessage(this.$t('documentEditor.imageUpload.missingFileError'));
    });
    editor.on('customImageUploadMaxSizeExceedError', data => {
      WElmessage.showErrorMessage(this.$t('documentEditor.imageUpload.onSizeExceed', {
        imgMaxSize: data?.maxFileSizeMB
      }));
    });
    editor.on('customImageApiError', () => {
      WElmessage.showErrorMessage(this.$t('errors.default'));
    });
  }
  handleFlipCardEvents(editor) {
    editor.on('flipcard:insert-image', cardData => {
      this.flipCardEventContext = cardData;
      this.flipCardAddImgEventTriggered = true;
    });
    editor.on('flipcard:delete-card', cardData => {
      this.flipCardEventContext = cardData;
      this.flipCardDeleteEventTriggered = true;
    });
  }
  onSaveChanges() {
    if (this.initialText === this.liveText) {
      ElMessage({
        message: this.$t('accessibility.statement.edition.nochanges'),
        type: 'info',
        showClose: false
      });
      return;
    }
    this.$emit('onSaveChanges', this.liveText);
  }
  onCancelChanges() {
    confirmation(this.$t('accessibility.statement.edition.on-cancel.dialog-body'), '', this.$t('accessibility.statement.edition.on-cancel.confirm'), this.$t('accessibility.statement.edition.on-cancel.abort')).then(() => {
      this.onConfirmCancelChanges();
    });
  }
  onConfirmCancelChanges() {
    this.$emit('onCancelChanges');
  }
  handleBlurEvent() {
    this.$emit('blur', this.liveText);
  }
  onImgUpdate(uploadedImageUrl) {
    if (uploadedImageUrl && this.flipCardEventContext) {
      this.flipCardEventContext.editor.execCommand('flipcardInsertImage', false, {
        cardId: this.flipCardEventContext.cardId,
        face: this.flipCardEventContext.face,
        imageUrl: uploadedImageUrl
      });
    }
  }
  onFlipCardDialogClose() {
    this.flipCardEventContext = null;
    this.flipCardAddImgEventTriggered = false;
  }
  onCardDeleteConfirm() {
    if (this.flipCardEventContext) {
      this.flipCardEventContext.editor.execCommand('flipcardDelete', false, this.flipCardEventContext.cardId);
    }
    this.resetCardDeleteEventData();
  }
  onCardDeleteCancel() {
    this.resetCardDeleteEventData();
  }
  resetCardDeleteEventData() {
    this.flipCardEventContext = null;
    this.flipCardDeleteEventTriggered = false;
  }
  beforeDestroy() {
    this.editorInstance?.destroy();
  }
};
__decorate([Prop({
  default: 'tmce-editor-id'
})], WTinyMce.prototype, "editorId", void 0);
__decorate([Prop({
  default: null
})], WTinyMce.prototype, "customConfig", void 0);
__decorate([Prop({
  default: ''
})], WTinyMce.prototype, "text", void 0);
__decorate([Prop({
  default: true
})], WTinyMce.prototype, "displayEditorButtons", void 0);
__decorate([Prop()], WTinyMce.prototype, "saveLabel", void 0);
__decorate([Prop()], WTinyMce.prototype, "cancelLabel", void 0);
WTinyMce = __decorate([Component({
  components: {
    ConfirmDialog,
    FlipCardImageDialog,
    tinymceEditor: Editor,
    StandardButton
  },
  emits: ["onSaveChanges", "onCancelChanges", "blur"]
})], WTinyMce);
export default WTinyMce;